import {
  BookOpen,
  Check,
  ChevronDown,
  Code2,
  Feather,
  Gavel,
  Languages,
  Lightbulb,
  MessageSquare,
  Microscope,
  ScanSearch,
  Scissors,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { CHAT_MODES, findMode } from "../lib/modes";
import type { ChatModeId } from "../lib/types";

export const MODE_ICON: Record<ChatModeId, React.ReactNode> = {
  standard: <MessageSquare size={14} />,
  learn: <BookOpen size={14} />,
  story: <Feather size={14} />,
  brainstorm: <Lightbulb size={14} />,
  critique: <ScanSearch size={14} />,
  code: <Code2 size={14} />,
  analyze: <Microscope size={14} />,
  translate: <Languages size={14} />,
  summarize: <Scissors size={14} />,
  debate: <Gavel size={14} />,
};

interface ModeSelectorProps {
  mode: ChatModeId;
  onChange: (mode: ChatModeId) => void;
}

export function ModeSelector({ mode, onChange }: ModeSelectorProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const active = findMode(mode);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div className="mode-selector" ref={rootRef}>
      <button
        className={mode === "standard" ? "mode-trigger" : "mode-trigger active"}
        onClick={() => setOpen(!open)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={active.description}
      >
        {MODE_ICON[active.id]}
        <span>{active.label}</span>
        <ChevronDown size={12} />
      </button>
      {open && (
        <>
          <button
            className="popover-backdrop"
            aria-label="Chiudi"
            onClick={() => setOpen(false)}
          />
          <div className="mode-popover" role="listbox">
            <div className="mode-popover-head">Modalità</div>
            {CHAT_MODES.map((item) => (
              <button
                key={item.id}
                role="option"
                aria-selected={item.id === mode}
                className={item.id === mode ? "mode-item active" : "mode-item"}
                onClick={() => {
                  onChange(item.id);
                  setOpen(false);
                }}
              >
                <span className="mode-item-glyph">{MODE_ICON[item.id]}</span>
                <span className="mode-item-body">
                  <strong>{item.label}</strong>
                  <small>{item.tagline}</small>
                </span>
                {item.id === mode && <Check size={13} />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
